/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Vault envelope: encrypt/decrypt orchestration for the two-key pattern.
 *
 *   passphrase ──[Argon2id(salt)]──► KEK ──[XChaCha20-Poly1305(wrapNonce, AAD_WRAP)]──► wrappedKey
 *                                                                                          │
 *                                                                                          ▼
 *                                              random vault key ──[XChaCha20-Poly1305(dataNonce, AAD_DATA)]──► ciphertext
 *
 * The two-key pattern lets a passphrase change re-wrap only the vault key
 * (~48 B re-encryption) instead of re-encrypting the entire VaultContents.
 *
 * Both encryptions use AADs derived from the same authenticated header
 * with distinct domain bytes (see `aad.ts`). A wrong passphrase produces
 * the same error class as a tampered envelope — the caller cannot tell
 * the difference (denying an oracle for either failure mode).
 */

import { base64nopad } from '@scure/base';
import { argon2id, ARGON2ID_DEFAULTS } from '../crypto/kdf.js';
import {
  encrypt as aeadEncrypt,
  decrypt as aeadDecrypt,
} from '../crypto/aead.js';
import { defaultRandom, type Random } from '../random.js';
import {
  CIPHER_ALGORITHM,
  ENVELOPE_VERSION,
  KDF_ALGORITHM,
  VaultContentsSchema,
  serializeVaultContents,
  type EnvelopeOuter,
  type EnvelopeOuterSerialized,
  type VaultContents,
} from './schema.js';
import { parseEnvelope } from './parse.js';
import {
  buildAadData,
  buildAadWrap,
  CIPHER_ID_XCHACHA20_POLY1305,
  KDF_ID_ARGON2ID,
  type AadHeader,
} from './aad.js';
import { checkAndPersist, type RollbackTracker } from './rollback.js';

const VAULT_KEY_BYTES = 32;
const NONCE_BYTES = 24;
const SALT_BYTES = 16;

/**
 * Thrown when an envelope fails to open. Wrong passphrase, tampered
 * header, tampered ciphertext, mis-validated schema — all surface as this
 * single class, intentionally. The caller cannot distinguish "wrong key"
 * from "modified ciphertext" from "malformed JSON" — denying a side-channel
 * oracle that a tampered-vs-wrong-passphrase distinction would create.
 */
export class EnvelopeOpenError extends Error {
  constructor(message = 'envelope failed to open') {
    super(message);
    this.name = 'EnvelopeOpenError';
  }
}

export interface EncryptParams {
  passphrase: string;
  contents: VaultContents;
  /** Optional injected RNG; defaults to `defaultRandom`. */
  rng?: Random;
  /** Optional override; defaults to a fresh 16-byte random ID. */
  envelopeId?: string;
  /** Caller-supplied; defaults to 0. F7 (#47) owns monotonic enforcement. */
  counter?: number;
  /** Optional override of Argon2id cost params (salt is always fresh). */
  kdfParams?: {
    memoryKiB?: number;
    iterations?: number;
    parallelism?: number;
  };
}

export interface DecryptParams {
  passphrase: string;
  envelope: EnvelopeOuterSerialized | EnvelopeOuter | Uint8Array | string;
  /**
   * Optional rollback tracker. When provided, the AAD-bound counter from
   * the just-decrypted envelope is checked against the tracker's stored
   * high-water mark; a stale counter throws `RollbackDetectedError`.
   * Distinct from `EnvelopeOpenError` because rollback can only be
   * detected after AEAD verification — by that point the bytes are
   * authentic, so distinguishing rollback from tamper reveals nothing
   * the attacker doesn't already know, and the user genuinely needs the
   * distinction to react appropriately.
   */
  tracker?: RollbackTracker;
}

const fillZero = (b: Uint8Array): void => {
  b.fill(0);
};

/**
 * Discriminator for `EnvelopeOuter` (post-Zod-transform, byte fields are
 * `Uint8Array`) vs `EnvelopeOuterSerialized` (transport shape, byte fields
 * are base64 strings). Tested on `kdf.salt` which is the canonical byte
 * field — `Uint8Array` ⇒ already parsed; anything else ⇒ serialized form.
 */
const isParsedEnvelopeOuter = (env: unknown): env is EnvelopeOuter => {
  if (env === null || typeof env !== 'object' || Array.isArray(env)) {
    return false;
  }
  const e = env as Record<string, unknown>;
  if (e.kdf === null || typeof e.kdf !== 'object') return false;
  const kdf = e.kdf as Record<string, unknown>;
  return kdf.salt instanceof Uint8Array;
};

/**
 * Build the typed AAD header from concrete parameters. Centralised so
 * encrypt and decrypt paths cannot disagree on field ordering.
 */
const aadHeaderFor = (params: {
  envelopeId: string;
  salt: Uint8Array;
  memoryKiB: number;
  iterations: number;
  parallelism: number;
  cipherNonce: Uint8Array;
  wrapNonce: Uint8Array;
  counter: number;
}): AadHeader => ({
  version: ENVELOPE_VERSION,
  envelopeId: params.envelopeId,
  kdf: {
    algorithmId: KDF_ID_ARGON2ID,
    salt: params.salt,
    memoryKiB: params.memoryKiB,
    iterations: params.iterations,
    parallelism: params.parallelism,
  },
  cipher: {
    algorithmId: CIPHER_ID_XCHACHA20_POLY1305,
    nonce: params.cipherNonce,
  },
  wrapNonce: params.wrapNonce,
  counter: params.counter,
});

/**
 * Encrypt `VaultContents` into a serializable envelope. The returned
 * object can be `JSON.stringify`'d for transport.
 *
 * **envelopeId precedence (#117).** When `params.envelopeId` is
 * omitted, defaults to `params.contents.envelopeId`. The previous
 * code defaulted to a fresh random — which diverged from the inner
 * `VaultContents.envelopeId`, so the rollback tracker keyed off a
 * value the inner payload didn't know about and rollback protection
 * silently broke on next open. When both are supplied they must
 * agree; mismatch throws (caller bug, not a tie-breaker).
 *
 * **Key hygiene (#118).** `kek` and `vaultKey` are zeroed in a
 * `try/finally` block, mirroring the existing pattern in `decrypt`.
 * If `argon2id` or either `aeadEncrypt` call (or `JSON.stringify`)
 * throws, the key bytes still hit `fillZero` on the way out — they
 * don't linger in memory waiting on GC. Best-effort hygiene; JS
 * makes no guarantees about residual GC copies.
 *
 * @throws Error from the underlying Argon2id or AEAD wrappers on
 *   parameter-validation failure, or when `params.envelopeId`
 *   conflicts with `params.contents.envelopeId`.
 */
export const encrypt = async (
  params: EncryptParams
): Promise<EnvelopeOuterSerialized> => {
  const rng = params.rng ?? defaultRandom;
  // #117: prefer caller-supplied envelopeId, fall back to inner contents.
  // A random outer id with a stable inner id breaks rollback tracking.
  if (
    params.envelopeId !== undefined &&
    params.envelopeId !== params.contents.envelopeId
  ) {
    throw new Error(
      `envelope.encrypt: params.envelopeId '${params.envelopeId}' ` +
        `disagrees with params.contents.envelopeId '${params.contents.envelopeId}'`
    );
  }
  const envelopeId = params.envelopeId ?? params.contents.envelopeId;
  const counter = params.counter ?? 0;
  const memoryKiB = params.kdfParams?.memoryKiB ?? ARGON2ID_DEFAULTS.memoryKiB;
  const iterations =
    params.kdfParams?.iterations ?? ARGON2ID_DEFAULTS.iterations;
  const parallelism =
    params.kdfParams?.parallelism ?? ARGON2ID_DEFAULTS.parallelism;

  const salt = rng.getRandomBytes(SALT_BYTES);
  const cipherNonce = rng.getRandomBytes(NONCE_BYTES);
  const wrapNonce = rng.getRandomBytes(NONCE_BYTES);

  // #118: key bytes live inside try/finally so an exception anywhere
  // below (argon2id, JSON.stringify, either aeadEncrypt) still hits
  // the `fillZero` path. Mirrors decrypt(). The `Uint8Array`-typed
  // mutable bindings stay `undefined` until allocation succeeds —
  // the finally guards each `fillZero` with an existence check.
  let kek: Uint8Array | undefined;
  let vaultKey: Uint8Array | undefined;
  try {
    kek = await argon2id({
      passphrase: params.passphrase,
      salt,
      memoryKiB,
      iterations,
      parallelism,
      outputLength: 32,
    });

    vaultKey = rng.getRandomBytes(VAULT_KEY_BYTES);

    const aadHeader = aadHeaderFor({
      envelopeId,
      salt,
      memoryKiB,
      iterations,
      parallelism,
      cipherNonce,
      wrapNonce,
      counter,
    });

    const wrappedKey = aeadEncrypt({
      key: kek,
      nonce: wrapNonce,
      plaintext: vaultKey,
      aad: buildAadWrap(aadHeader),
    });

    const innerJson = new TextEncoder().encode(
      JSON.stringify(serializeVaultContents(params.contents))
    );

    const ciphertext = aeadEncrypt({
      key: vaultKey,
      nonce: cipherNonce,
      plaintext: innerJson,
      aad: buildAadData(aadHeader, wrappedKey),
    });

    return {
      version: ENVELOPE_VERSION,
      envelopeId,
      kdf: {
        algorithm: KDF_ALGORITHM,
        salt: base64nopad.encode(salt),
        memoryKiB,
        iterations,
        parallelism,
      },
      cipher: {
        algorithm: CIPHER_ALGORITHM,
        nonce: base64nopad.encode(cipherNonce),
      },
      wrappedKey: base64nopad.encode(wrappedKey),
      wrappedKeyNonce: base64nopad.encode(wrapNonce),
      ciphertext: base64nopad.encode(ciphertext),
      counter,
    };
  } finally {
    // Best-effort zeroing on every exit path — including thrown ones.
    if (kek) fillZero(kek);
    if (vaultKey) fillZero(vaultKey);
  }
};

/**
 * Decrypt an envelope back to `VaultContents`. Wrong passphrase, tampered
 * header bytes, tampered ciphertext, malformed inner JSON, and Zod
 * validation failures all surface as `EnvelopeOpenError` — the caller
 * cannot tell which.
 *
 * If a `tracker` is supplied, the AAD-bound counter is checked against
 * the tracker's stored high-water mark *after* successful AEAD
 * verification. A stale counter throws `RollbackDetectedError` (a
 * distinct class — by the time it fires we know the bytes are
 * authentic, so the user needs the distinction).
 *
 * @throws {EnvelopeOpenError} on any decryption / validation failure
 * @throws {RollbackDetectedError} when tracker is supplied and the
 *   envelope's counter is older than the stored high-water mark
 */
export const decrypt = async (
  params: DecryptParams
): Promise<VaultContents> => {
  let parsed: EnvelopeOuter;
  try {
    // Three input shapes are accepted (per DecryptParams):
    //   1. Uint8Array / string  → run through parseEnvelope (full pipeline)
    //   2. EnvelopeOuter        → already-parsed object with Uint8Array byte
    //                             fields. Trust it; do NOT JSON.stringify
    //                             (Uint8Array would serialize as
    //                             {"0":1,"1":2,...}, breaking parseEnvelope's
    //                             base64 expectations).
    //   3. EnvelopeOuterSerialized → object with base64 string byte fields.
    //                             Stringify and re-validate.
    //
    // parseEnvelope's typed errors are converted to EnvelopeOpenError below
    // to preserve the no-oracle property; the parser layer keeps the typed
    // hierarchy for callers that need it (e.g. the fuzz harness).
    if (
      typeof params.envelope === 'string' ||
      params.envelope instanceof Uint8Array
    ) {
      parsed = parseEnvelope(params.envelope);
    } else if (isParsedEnvelopeOuter(params.envelope)) {
      parsed = params.envelope;
    } else {
      parsed = parseEnvelope(JSON.stringify(params.envelope));
    }
  } catch {
    throw new EnvelopeOpenError('envelope schema validation failed');
  }

  let kek: Uint8Array | undefined;
  let vaultKey: Uint8Array | undefined;
  let contents: VaultContents;
  try {
    // All failure modes — wrong passphrase, tampered header, tampered
    // ciphertext, malformed JSON, schema rejection, KDF parameter
    // rejection — collapse into the same EnvelopeOpenError class. The
    // caller cannot distinguish them, denying any side-channel oracle.
    kek = await argon2id({
      passphrase: params.passphrase,
      salt: parsed.kdf.salt,
      memoryKiB: parsed.kdf.memoryKiB,
      iterations: parsed.kdf.iterations,
      parallelism: parsed.kdf.parallelism,
      outputLength: 32,
    });

    const aadHeader = aadHeaderFor({
      envelopeId: parsed.envelopeId,
      salt: parsed.kdf.salt,
      memoryKiB: parsed.kdf.memoryKiB,
      iterations: parsed.kdf.iterations,
      parallelism: parsed.kdf.parallelism,
      cipherNonce: parsed.cipher.nonce,
      wrapNonce: parsed.wrappedKeyNonce,
      counter: parsed.counter,
    });

    vaultKey = aeadDecrypt({
      key: kek,
      nonce: parsed.wrappedKeyNonce,
      ciphertext: parsed.wrappedKey,
      aad: buildAadWrap(aadHeader),
    });

    const plaintext = aeadDecrypt({
      key: vaultKey,
      nonce: parsed.cipher.nonce,
      ciphertext: parsed.ciphertext,
      aad: buildAadData(aadHeader, parsed.wrappedKey),
    });

    const parsedJson = JSON.parse(new TextDecoder().decode(plaintext));
    contents = VaultContentsSchema.parse(parsedJson);
  } catch {
    throw new EnvelopeOpenError();
  } finally {
    if (kek) fillZero(kek);
    if (vaultKey) fillZero(vaultKey);
  }

  // Rollback check runs *after* AEAD verification has succeeded — so
  // RollbackDetectedError is not caught and converted to
  // EnvelopeOpenError. The counter we're checking is AAD-bound and
  // therefore authentic at this point.
  if (params.tracker) {
    checkAndPersist(parsed.envelopeId, parsed.counter, params.tracker);
  }

  return contents;
};
